/**
 * GameTheme — the global, swappable look-and-feel of the game.
 *
 * Today a theme only carries UI sounds, and only one theme ("default") exists.
 * The point of this indirection is the future: a seasonal theme (Halloween,
 * holidays, an event drop) becomes a single new GameTheme whose fields override
 * the default — swap the active theme and the whole game re-skins through it,
 * with no call-site changes. As we add themeable surfaces (palette, particles,
 * music beds, card art) they grow as fields on this interface, resolved the same
 * way the click sound is below.
 *
 * Resolution rule: anything themeable reads from `getActiveTheme()` at use time,
 * never caches a SoundId/colour at module load. That keeps a live theme swap
 * (e.g. an event going live mid-session) honest everywhere.
 */

import { SoundId } from '../../engine/audio/sound';

export interface GameTheme {
  /** Stable id used for persistence / selection ('default', 'halloween', …). */
  id: string;
  /** Display name (player-visible once theme selection ships). */
  name: string;
  sounds: {
    /**
     * Default click for any clickable UI (icon button, tab, styled div) that
     * doesn't declare its own `data-sound`. See uiClickSounds.ts for the
     * delegator.
     */
    click: SoundId;
    /**
     * Press for visible, text-labelled buttons. Optional: a theme that omits it
     * falls back to `click`, so a partial seasonal theme still works.
     */
    button?: SoundId;
    // Future roles slot in here (back, toggle, tabSwitch, hover, …); add the
    // field, point the default theme at the existing SoundId, and themes can
    // override per-role. Keep them optional so a partial theme falls back.
  };
}

/** The base theme. The only theme for now; every other theme overrides from here. */
export const DEFAULT_THEME: GameTheme = {
  id: 'default',
  name: 'Default',
  sounds: {
    click: SoundId.UI_TAP,
    button: SoundId.UI_BUTTON,
  },
};

let active: GameTheme = DEFAULT_THEME;

/** The theme everything themeable resolves through right now. */
export function getActiveTheme(): GameTheme {
  return active;
}

/**
 * Swap the live theme. Safe to call any time — themeable surfaces read on use,
 * so the next click / next render picks up the new theme. (A future event drop
 * can flip this from a server flag without reloading.)
 */
export function setActiveTheme(theme: GameTheme): void {
  active = theme;
}
